/**
* Prepares a string containing LaTeX for rendering with react-markdown and KaTeX.
* 1. Fixes JSON-escaped backslashes in commands (e.g., \\lim -> \lim).
* 2. Converts JSON-escaped standard LaTeX delimiters to dollar delimiters
*    (e.g., \\( -> $, \\) -> $, \\[ -> $$, \\] -> $$).
* 3. Trims whitespace around and immediately inside math delimiters.
* 4. Removes the explicit \n -> \n\n conversion, relying on Markdown's default.
*
* @param {string | null | undefined} text The input text from the API.
* @returns {string} The processed text ready for ReactMarkdown.
*/
export const prepareLatexForMarkdown = (text) => {
    if (!text || typeof text !== 'string') return '';
    let processed = text.replace(/\\\\/g, '\\');
    processed = processed.replace(/\\\(/g, '$');
    processed = processed.replace(/\\\)/g, '$');
    processed = processed.replace(/\\\[/g, '$$');
    processed = processed.replace(/\\\]/g, '$$');
    processed = processed.replace(
      /\$\s*([\s\S]*?)\s*\$/g,
      (match, group1) => `$${group1.trim()}$`
    );
    processed = processed.replace(
      /\$\$\s*([\s\S]*?)\s*\$\$/g,
      (match, group1) => `$$${group1.trim()}$$`
    );
    processed = processed.replace(/\$\s*\$/g, '');
    processed = processed.replace(/\$\$\s*\$\$/g, '');
    return processed;
  };
  /**
   * Removes any text within [tag]...[/tag] from a string.
   * This is used for creating clean previews of text that may contain formatting tags.
   * @param {string | null | undefined} text The input text.
   * @returns {string} The text with tags removed.
   */
  export const stripTagsForPreview = (text) => {
    if (!text || typeof text !== 'string') return '';
    const tagRegex = /\[([^\]]+)\][\s\S]*?\[\/\1\]/g;
    return text.replace(tagRegex, ' ').trim();
  };
  /**
   * NEW: Strips Markdown and other non-verbal syntax for clean TTS input.
   * @param {string | null | undefined} text The input text.
   * @returns {string} The sanitized text ready for speech synthesis.
   */
  export const sanitizeTextForTts = (text) => {
    if (!text || typeof text !== 'string') return '';
    let sanitized = text;
    sanitized = sanitized.replace(/\[(.*?)\]\(.*?\)/g, '$1');
    sanitized = sanitized.replace(/!\[(.*?)\]\(.*?\)/g, '$1');
    sanitized = sanitized.replace(/(\*\*|__)(.*?)\1/g, '$2');
    sanitized = sanitized.replace(/(\*|_)(.*?)\1/g, '$2');
    sanitized = sanitized.replace(/##+\s/g, '');
    sanitized = sanitized.replace(/^\s*[-*]\s+/gm, '');
    sanitized = sanitized.replace(/^\s*\d+\.\s+/gm, '');
    sanitized = sanitized.replace(/\$+(.*?)\$+/g, '$1');
    sanitized = sanitized.replace(/---/g, '');
    sanitized = sanitized.replace(/\\\\/g, '\\');
    return sanitized.trim();
  };
  /**
   * Replaces resource ID tags with readable names for TTS or plain text display.
   * @param {string} text The raw text containing tags.
   * @param {function} getResourceById Function to retrieve resource details by ID.
   * @returns {string} The text with tags replaced by resource names.
   */
  export const replaceResourceTags = (text, getResourceById) => {
    if (!text || typeof text !== 'string') return '';
    
    let processed = text;
    
    // Replace [RESOURCE]id[/RESOURCE]
    processed = processed.replace(/\[RESOURCE\](.*?)\[\/RESOURCE\]/gi, (match, id) => {
        const cleanId = id.trim();
        const res = getResourceById(cleanId);
        return res?.title || res?.name || 'Resource';
    });

    // Replace [A]id[/A], [E]id[/E], etc.
    processed = processed.replace(/\[([AELPS])\](.*?)\[\/\1\]/gi, (match, tag, id) => {
        const cleanId = id.trim();
        const res = getResourceById(cleanId);
        if (res) {
          return res.name || res.title || 'Item';
        }
        // Fallback for missing resources to avoid reading UUIDs
        const typeMap = {
          'A': 'Activity',
          'E': 'Equipment',
          'L': 'Location',
          'P': 'Plan',
          'S': 'Session'
        };
        return typeMap[tag.toUpperCase()] || 'Item';
    });

    return processed;
  };

  /**
   * DEPRECATED: Original preprocessing function, kept for reference.
   */
  export const preprocessTextForMarkdown_DEPRECATED = (text) => {
      console.warn("preprocessTextForMarkdown_DEPRECATED is called. Use prepareLatexForMarkdown for fixing JSON escapes before rendering.");
      if (!text || typeof text !== 'string') return '';
      return text;
  };